import { useEffect } from "react";
import {
  Form,
  Link,
  Navigate,
  useLoaderData,
  useActionData,
  useNavigation,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { UpgradePrompt, Button } from "../../components/common";
import { useAuth } from "../../hooks/useAuth";
import type { MembersData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import styles from "./InviteMemberPage.module.css";

export default function InviteMemberPage() {
  const { organization, permissions } = useLoaderData() as MembersData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const { user } = useAuth();

  const isSubmitting = navigation.state === "submitting";

  // Get user role for showing owner option in role select
  const userOrg = user?.organizations.find((o) => o.id === organization.id);
  const userRole = userOrg?.role || null;

  useEffect(() => {
    if (actionData?.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  // Use server-computed permissions for access control
  if (!permissions.canManageMembers) {
    return <Navigate to={`/organizations/${organization.id}`} replace />;
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.back}>
            <Link to={`/organizations/${organization.id}`} className={styles.backLink}>
              ← Back to Organization
            </Link>
          </div>

          <div className={styles.card}>
            <h1>Invite Member</h1>
            <p className={styles.description}>Invite a new member to join this organization.</p>

            {!permissions.canInviteMember ? (
              <UpgradePrompt
                organizationId={organization.id}
                title="Seat Limit Reached"
                message="Your organization has reached its member limit. Upgrade to Pro to invite more members."
              />
            ) : (
              <Form method="post" className={styles.form}>
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.email ? styles.formGroupError : ""}`}
                >
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="user@example.com"
                    disabled={isSubmitting}
                  />
                  {actionData?.fieldErrors?.email && (
                    <span className={styles.formError}>{actionData.fieldErrors.email}</span>
                  )}
                </div>

                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.role ? styles.formGroupError : ""}`}
                >
                  <label htmlFor="role">Role</label>
                  <select id="role" name="role" defaultValue="member" disabled={isSubmitting}>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    {userRole === "owner" && <option value="owner">Owner</option>}
                  </select>
                  {actionData?.fieldErrors?.role && (
                    <span className={styles.formError}>{actionData.fieldErrors.role}</span>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Inviting..."
                >
                  Invite Member
                </Button>
              </Form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
